import { DealerGroup, DealerGroupMember, DealerGroupsState } from '../../interfaces/dealer-group.interface';
import { reduceToRecord } from '../../../../util/util';

export const enum DealerGroupActions {
  TOGGLE_GROUP = 'TOGGLE_GROUP',
  TOGGLE_MEMBER = 'SELECT_MEMBER',
  TOGGLE_ALL = 'TOGGLE_ALL',
  SET_STATE = 'SET_STATE'
}

export class ToggleGroup {
  readonly type = DealerGroupActions.TOGGLE_GROUP;
  constructor(readonly uuid: string) {}
}

export class ToggleMember {
  readonly type = DealerGroupActions.TOGGLE_MEMBER;
  constructor(readonly uuid: string) {}
}

export class ToggleAll {
  readonly type = DealerGroupActions.TOGGLE_ALL;
}

export class SetState {
  readonly type = DealerGroupActions.SET_STATE;
  constructor(readonly state: DealerGroupsState) {}
}

export type DealerGroupAction = ToggleGroup | ToggleMember | ToggleAll | SetState;

export type Selector<T> = (state: DealerGroupsState) => T;

export interface Selectable {
  isSelected: boolean;
}

function updateEntities<E>(ids: string[], record: Record<string, E>, updateFn: (entity: E) => E) {
  return ids.map(id => updateFn(record[id]));
}

function setIsSelected<E extends Selectable>(entity: E) {
  return { ...entity, isSelected: true };
}

function setIsNotSelected<E extends Selectable>(entity: E) {
  return { ...entity, isSelected: false };
}

export function selectAllMembersSelected(state: DealerGroupsState) {
  return Object.values(state.memberEntities).every(m => m.isSelected);
}

export function selectGroupIsSelected(groupUuid: string): Selector<boolean> {
  return state => selectGroupMembers(groupUuid)(state).every(member => member.isSelected);
}

export function selectGroupMember(oeUuid: string): Selector<DealerGroupMember> {
  return state => state.memberEntities[oeUuid];
}

export function selectGroup(uuid: string): Selector<DealerGroup> {
  return state => state.groupEntities[uuid];
}

export function selectGroupMembers(groupUuid: string): Selector<DealerGroupMember[]> {
  return state => {
    const group = selectGroup(groupUuid)(state);
    return !group ? [] : group.memberOeUuids.map(uuid => selectGroupMember(uuid)(state));
  };
}

export function selectGroupMemberSelectedCount(groupUuid: string): Selector<number> {
  return state => selectGroupMembers(groupUuid)(state).filter(member => member.isSelected).length;
}

export enum DealerDropdownDisplayText {
  ALL_GROUPS = 'ALL_GROUPS',
  NO_GROUPS = 'NO_GROUPS',
  MULTI_GROUPS = 'MULTI_GROUPS'
}

export function selectDisplayText(state: DealerGroupsState): string {
  if (selectDealerGroupCount(state) && selectDealerGroupCount(state) > 1) {
    return selectDisplayTextMultipleGroup(state);
  }
  return selectDisplayTextSingleGroup(state);
}

export function selectSelectedDealersCount(state: DealerGroupsState): number {
  return Object.values(state.memberEntities).filter(m => m.isSelected).length;
}

export function selectHasMembers(state: DealerGroupsState): boolean {
  return state.memberUuids.length > 0;
}

export function reducer(state: DealerGroupsState, action: DealerGroupAction): DealerGroupsState {
  switch (action.type) {
  case DealerGroupActions.TOGGLE_ALL: {
    const allSelected = selectAllMembersSelected(state);
    const updateFn = allSelected ? setIsNotSelected : setIsSelected;
    return {
      ...state,
      memberEntities: reduceToRecord(updateEntities(state.memberUuids, state.memberEntities, updateFn), 'oeUuid')
    };
  }
  case DealerGroupActions.TOGGLE_GROUP: {
    const group = selectGroup(action.uuid)(state);
    const groupIsSelected = selectGroupIsSelected(group.uuid)(state);
    const selectFn = groupIsSelected ? setIsNotSelected : setIsSelected;
    return {
      ...state,
      memberEntities: {
        ...state.memberEntities,
        ...reduceToRecord(updateEntities(group.memberOeUuids, state.memberEntities, selectFn), 'oeUuid')
      }
    };
  }
  case DealerGroupActions.TOGGLE_MEMBER: {
    const member = selectGroupMember(action.uuid)(state);
    const selectFn = member.isSelected ? setIsNotSelected : setIsSelected;
    return {
      ...state,
      memberEntities: { ...state.memberEntities, [action.uuid]: selectFn(state.memberEntities[action.uuid]) }
    };
  }
  case DealerGroupActions.SET_STATE: {
    return { ...action.state };
  }
  default:
    return state;
  }
}

export function selectedGroupMemberUuids(state: DealerGroupsState) {
  return Object.values(state.memberEntities).filter(m => m.isSelected).map(m => m.oeUuid);
}

export function unselectedMemberUuid(state: DealerGroupsState) {
  return Object.values(state.memberEntities).filter(m => !m.isSelected).map(m => m.oeUuid);
}

export function selectedGroupUuid(state: DealerGroupsState) {
  return  Object.values(state.groupEntities)
    .map(group => {
      if (selectGroupIsSelected(group.uuid)(state)) {
        return group.uuid;
      }
      return selectGroupMembers(group.uuid)(state).some(member => member.isSelected) ? group.uuid : undefined;
    })
    .filter(groupUuid => typeof groupUuid === 'string');
}


export function selectDealerGroupCount(state: DealerGroupsState): number {
  return Object.values(state.groupEntities).length;
}

export function selectSingleDealerGroupName(state: DealerGroupsState): string {
  if (selectDealerGroupCount(state) === 1) {
    return Object.values(state.groupEntities).map(grp=> grp.groupName)[0];
  }
  return null;
}

export function selectDisplayTextSingleGroup(state: DealerGroupsState): string {

  let displayText;
  const selectedGroupUuids = selectedGroupUuid(state);
  if (selectedGroupUuids.length === 1) {
    return selectGroup(selectedGroupUuids[0])(state).groupName;
  }
   if (selectDealerGroupCount(state) === 1 && selectedGroupUuids.length === 0) {
    displayText =  Object.values(state.groupEntities).map(grp=> grp.groupName)[0];
  }
  return displayText;
}

export function selectDisplayTextMultipleGroup(state: DealerGroupsState): string {

  if (selectAllMembersSelected(state)) {
    return DealerDropdownDisplayText.ALL_GROUPS;
  }
  const selectedGroupUuids = selectedGroupUuid(state);

  if (selectedGroupUuids.length === 1) {
    return selectGroup(selectedGroupUuids[0])(state).groupName;
  }

  if (selectedGroupUuids.length === 0) {
    return DealerDropdownDisplayText.NO_GROUPS;
  }
  return DealerDropdownDisplayText.MULTI_GROUPS;
}
